import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Loader2, Check, X, Wand2, Scissors, AlignLeft, SpellCheck, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { ApiResponse } from '@/types';
import { cn } from '@/lib/utils';
import { sanitizeInlineAiField } from '@/lib/ai-inline-field';
import { toast } from 'sonner';

interface AIAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  instruction?: string;
  isGenerate?: boolean;
}

const ACTIONS: AIAction[] = [
  {
    id: 'improve',
    label: 'Verbessern',
    icon: <Wand2 className="h-3.5 w-3.5" />,
    instruction:
      'Verbessere diesen Text: klarere Formulierungen, weniger Worthülsen, stärkere Aussagen. Keine neuen erfundenen Fakten. Gleiche Sprache beibehalten.',
  },
  {
    id: 'shorten',
    label: 'Kürzen',
    icon: <Scissors className="h-3.5 w-3.5" />,
    instruction:
      'Kürze auf das Wesentliche; entferne Füllsätze und Allgemeinplätze. Kernaussage und Sprache beibehalten.',
  },
  {
    id: 'expand',
    label: 'Ausführlicher',
    icon: <AlignLeft className="h-3.5 w-3.5" />,
    instruction:
      'Erweitere nur mit sinnvollen, konkreten Details zum Kontext — keine generische Marketing-Plattitüden. Gleiche Sprache.',
  },
  {
    id: 'correct',
    label: 'Rechtschreibung & Stil',
    icon: <SpellCheck className="h-3.5 w-3.5" />,
    instruction: 'Korrigiere Rechtschreibung, Grammatik und Zeichensetzung; behalte den Inhalt bei. Gleiche Sprache.',
  },
  {
    id: 'generate',
    label: 'Neu generieren',
    icon: <RefreshCw className="h-3.5 w-3.5" />,
    isGenerate: true,
  },
];

export interface AITextHelperProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Plain text for AI requests when `value` is HTML or otherwise not plain (e.g. rich text). */
  aiTextValue?: string;
  context?: string;
  fieldLabel?: string;
  disabled?: boolean;
  className?: string;
  placement?: 'inline' | 'textarea';
}

export function AITextHelper({
  value,
  onValueChange,
  aiTextValue,
  context,
  fieldLabel,
  disabled,
  className,
  placement = 'textarea',
}: AITextHelperProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sourceForAi = aiTextValue ?? value;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const runAction = async (action: AIAction) => {
    setOpen(false);
    setLoading(true);
    setActiveAction(action.id);
    setResult(null);

    const applyInline = (raw: string | null | undefined): string | null => {
      if (raw == null) return null;
      if (placement !== 'inline') return raw;
      const cleaned = sanitizeInlineAiField(raw);
      if (!cleaned.trim()) {
        toast.error('Kein brauchbarer Kurztext — bitte erneut versuchen oder manuell eingeben.');
        return null;
      }
      return cleaned;
    };

    try {
      if (action.isGenerate) {
        if (placement === 'inline') {
          const prompt = value.trim()
            ? `Erstelle einen sehr kurzen, eindeutigen Namen oder Titel für das Feld «${fieldLabel ?? 'dieses Feld'}». Kontext: ${context ?? ''}. Aktueller Entwurf (nur Orientierung, nicht wörtlich übernehmen): ${value}`
            : `Erstelle einen sehr kurzen, eindeutigen Namen oder Titel für das Feld «${fieldLabel ?? 'dieses Feld'}».${context ? ` Kontext: ${context}` : ''}`;

          const res = await api.post<ApiResponse<{ content: string }>>('/ai/generate', {
            prompt,
            type: 'short_label',
          });
          setResult(applyInline(res.data?.content ?? null));
        } else {
          const field = fieldLabel ?? 'dieses Feld';
          const ctx = (context ?? '').trim() || '(kein zusätzlicher Kontext)';
          const prompt = sourceForAi.trim()
            ? `Feld: «${field}»

Aufgabe: Schreibe einen präzisen, umsetzbaren Text für dieses Feld. Geh direkt zur Sache: keine Einleitung, keine leeren Phrasen („steigern Sie Ihre Präsenz“, „nachhaltig erfolgreich“ ohne Inhalt).

Nutze den Kontext und die vorhandene Version: überarbeite oder ersetze sie inhaltlich, sodass Lieferanten und Leser sofort verstehen, was gemeint ist.

Kontext:
${ctx}

Vorhandene Version (überarbeiten oder ersetzen):
${sourceForAi}`
            : `Feld: «${field}»

Aufgabe: Schreibe einen präzisen, umsetzbaren Text für dieses Feld. Geh direkt zur Sache; keine Worthülsen.

Kontext:
${ctx}`;

          const res = await api.post<ApiResponse<{ content: string }>>('/ai/generate', {
            prompt,
            type: 'marketing_copy',
            tone: 'sachlich, konkret, ohne Marketing-Floskeln',
            length: 'medium',
          });
          setResult(res.data?.content ?? null);
        }
      } else {
        const baseInstruction = context
          ? `${action.instruction} Dieses Feld ist für: ${context}.`
          : action.instruction;
        const contextualInstruction =
          placement === 'inline'
            ? `${baseInstruction} Antwort nur als kurzer Einzeiler (höchstens ca. 80 Zeichen), ohne Markdown, Sternchen oder Fließtext — nur der fertige Text.`
            : baseInstruction;

        const res = await api.post<ApiResponse<{ improved: string; content?: string }>>('/ai/improve', {
          text: sourceForAi,
          instruction: contextualInstruction,
        });
        setResult(applyInline(res.data?.improved ?? res.data?.content ?? null));
      }
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  const handleAccept = () => {
    if (result) {
      onValueChange(result);
    }
    setResult(null);
  };

  const handleDiscard = useCallback(() => {
    setResult(null);
  }, []);

  useEffect(() => {
    if (result === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDiscard();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [result, handleDiscard]);

  if (disabled) return null;

  const hasTextForActions = (placement === 'textarea' ? sourceForAi : value).trim().length > 0;

  return (
    <div
      className={cn(
        'relative pointer-events-none',
        /* Clicks pass through to textarea / TipTap; only the KI button + menus opt in. */
        className
      )}
      ref={dropdownRef}
    >
      <button
        type="button"
        onClick={() => {
          if (!loading) setOpen((prev) => !prev);
        }}
        disabled={loading}
        title="KI-Assistent"
        className={cn(
          'pointer-events-auto inline-flex items-center justify-center rounded-md transition-colors',
          'text-muted hover:bg-card-hover hover:text-accent',
          placement === 'textarea'
            ? 'absolute bottom-2 right-1.5 z-10 h-7 w-7 text-sm'
            : 'absolute right-1 top-1/2 z-10 h-7 w-7 -translate-y-1/2 text-sm',
          loading && 'cursor-not-allowed opacity-60'
        )}
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
      </button>

      {open && (
        <div className={cn(
          'pointer-events-auto absolute z-50 min-w-[180px] rounded-lg border border-border bg-card shadow-lg',
          placement === 'textarea'
            ? 'bottom-9 right-1.5'
            : 'right-1 top-9'
        )}>
          <div className="p-1">
            <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted">
              KI-Aktionen
            </p>
            {(placement === 'inline' ? ACTIONS.filter((a) => a.id !== 'expand') : ACTIONS).map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => void runAction(action)}
                disabled={action.id !== 'generate' && !hasTextForActions}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                  'text-muted hover:bg-card-hover',
                  action.id !== 'generate' && !hasTextForActions && 'cursor-not-allowed opacity-40'
                )}
              >
                <span className="text-muted">{action.icon}</span>
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {result !== null &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            {/* Radix modal sets body { pointer-events: none }; portals must opt back in or clicks never reach here */}
            <div
              className="pointer-events-auto fixed inset-0 z-[200] bg-foreground/75"
              aria-hidden
              onClick={handleDiscard}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="ai-suggestion-title"
              className="overlay-panel pointer-events-auto fixed left-1/2 top-1/2 z-[210] w-[min(100%-2rem,32rem)] max-h-[min(90vh,32rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-card p-5 shadow-overlay"
            >
              <div className="mb-3 flex items-center gap-2 border-b border-border pb-3">
                <Sparkles className="h-4 w-4 shrink-0 text-accent" />
                <span id="ai-suggestion-title" className="text-sm font-semibold text-foreground">
                  KI-Vorschlag
                </span>
              </div>
              <p className="max-h-[min(50vh,16rem)] min-w-0 overflow-y-auto break-words whitespace-pre-wrap text-sm text-foreground">
                {result}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="h-9 gap-1.5 px-4 text-xs"
                  onClick={handleAccept}
                >
                  <Check className="h-3.5 w-3.5" />
                  Übernehmen
                </Button>
                <Button type="button" size="sm" variant="outline" className="h-9 gap-1.5 px-4 text-xs" onClick={handleDiscard}>
                  <X className="h-3.5 w-3.5" />
                  Verwerfen
                </Button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
